import { Button, Stack, Text, TextInput } from '@mantine/core';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useSearchableOptions } from '../../hooks/use-searchable-options';
import { FilterOption, toVirtualCheckboxItems } from '../../utils/filter-options';
import {
  getEffectiveOrganisationSelectedIds,
  isAllOrganisationsSelected,
  isNoOrganisationSelected,
  toggleOrganisationInSelection,
} from '../../utils/organisation-id-set-filter';
import { sameIdSets } from '../../utils/id-set-filter';

import { FilterPopoverShell } from './filter-popover-shell';
import { VirtualCheckboxList } from './virtual-checkbox-list';

interface OrganisationFilterPopoverProps {
  organisationIds: number[];
  organisationOptions: FilterOption[];
  defaultOrganisationIds: number[];
  onOrganisationIdsChange: (ids: number[]) => void;
}

export const OrganisationFilterPopover: FC<OrganisationFilterPopoverProps> = ({
  organisationIds,
  organisationOptions,
  defaultOrganisationIds,
  onOrganisationIdsChange,
}) => {
  const { t } = useTranslation();
  const {
    debouncedSearch,
    filteredOptions,
    isSearchPending,
    resetSearch,
    search,
    setSearch,
  } = useSearchableOptions(organisationOptions);

  const allOrganisationIds = useMemo(
    () => organisationOptions.map((organisation) => organisation.id),
    [organisationOptions],
  );

  const isAllSelected = isAllOrganisationsSelected(
    organisationIds,
    allOrganisationIds,
  );
  const isNoneSelected = isNoOrganisationSelected(organisationIds);
  const effectiveSelectedIds = useMemo(
    () => getEffectiveOrganisationSelectedIds(organisationIds),
    [organisationIds],
  );

  const listItems = useMemo(
    () => toVirtualCheckboxItems(filteredOptions),
    [filteredOptions],
  );

  const badgeLabel = isAllSelected
    ? t('statistics.filter-all')
    : String(organisationIds.length);

  const toggleOrganisation = useCallback(
    (organisationId: number) => {
      onOrganisationIdsChange(
        toggleOrganisationInSelection(organisationIds, organisationId),
      );
    },
    [onOrganisationIdsChange, organisationIds],
  );

  const isDefaultSelection = sameIdSets(
    organisationIds,
    defaultOrganisationIds,
  );

  return (
    <FilterPopoverShell
      badgeLabel={badgeLabel}
      headerActions={
        <>
          <Button
            disabled={
              defaultOrganisationIds.length === 0 || isDefaultSelection
            }
            onClick={() => onOrganisationIdsChange([...defaultOrganisationIds])}
            size="compact-xs"
            variant="subtle"
          >
            {t('statistics.organisation-filter-reset')}
          </Button>
          <Button
            disabled={isAllSelected}
            onClick={() => onOrganisationIdsChange([...allOrganisationIds])}
            size="compact-xs"
            variant="subtle"
          >
            {t('statistics.filter-select-all')}
          </Button>
          <Button
            disabled={isNoneSelected}
            onClick={() => onOrganisationIdsChange([])}
            size="compact-xs"
            variant="subtle"
          >
            {t('statistics.filter-deselect-all')}
          </Button>
        </>
      }
      isAllSelected={isAllSelected}
      label={t('common.organisation')}
      onClose={resetSearch}
      title={t('common.search_organisation')}
      width={360}
    >
      <TextInput
        autoFocus
        onChange={(event) => setSearch(event.currentTarget.value)}
        placeholder={t('common.search_organisation')}
        value={search}
      />
      {organisationOptions.length === 0 ? (
        <Text c="dimmed" size="sm">
          {t('statistics.organisation-filter-empty')}
        </Text>
      ) : isSearchPending ? (
        <Text c="dimmed" size="sm">
          {t('statistics.organisation-filter-searching')}
        </Text>
      ) : filteredOptions.length === 0 ? (
        <Text c="dimmed" size="sm">
          {t('statistics.organisation-filter-no-results')}
        </Text>
      ) : (
        <Stack gap={4}>
          <Text c="dimmed" size="xs">
            {t('statistics.organisation-filter-count', {
              count: filteredOptions.length,
            })}
          </Text>
          <VirtualCheckboxList
            key={debouncedSearch}
            items={listItems}
            onToggle={toggleOrganisation}
            selectedIds={effectiveSelectedIds}
          />
        </Stack>
      )}
    </FilterPopoverShell>
  );
};

export default OrganisationFilterPopover;
